/**
 * Parse react-markdown data-* attributes using a declarative schema.
 *
 * react-markdown converts data-* attrs to camelCase (data-file-id → dataFileId).
 * Type coercion is inferred from the default value:
 *
 *   string  → value ?? default
 *   number  → Number(value) || default
 *   null    → nullable number (Number or null)
 *   false   → boolean (value === 'true')
 *
 * @example
 * parseDataAttrs<MyAttrs>(node.properties, {
 *   title: '',        // string
 *   pages: null,      // number | null
 *   size: 0,          // number
 *   enabled: false,   // boolean
 * });
 */
export const parseDataAttrs = <T>(
  properties: Record<string, any>,
  schema: Record<string, string | number | boolean | null>,
): T => {
  const result: Record<string, any> = {};

  for (const [key, defaultValue] of Object.entries(schema)) {
    const dataKey = `data${key.charAt(0).toUpperCase()}${key.slice(1)}`;
    const raw = properties[dataKey];

    if (defaultValue === null) {
      result[key] = raw != null ? Number(raw) : null;
    } else if (typeof defaultValue === 'boolean') {
      result[key] = raw === 'true';
    } else if (typeof defaultValue === 'number') {
      result[key] = Number(raw) || defaultValue;
    } else {
      result[key] = raw ?? defaultValue;
    }
  }

  return result as T;
};
